import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  adminListGallery,
  adminUpdateGallery,
  adminDeleteGallery,
  adminReorderGallery,
  adminBulkGallery,
  adminBackfillTech,
  adminPollBackfill,
  resolveMedia,
} from "@/lib/api";
import { toast } from "sonner";
import { videoThumbUrl } from "@/lib/video";
import {
  Plus, Eye, EyeOff, Star, Trash2, Pencil, Play, Clapperboard,
  GripVertical, Save, UploadCloud, CheckSquare, Square, XCircle, Sparkles, Loader2, RefreshCw,
} from "lucide-react";
import {
  DndContext, closestCenter, PointerSensor, KeyboardSensor,
  useSensor, useSensors,
} from "@dnd-kit/core";
import {
  arrayMove, SortableContext, rectSortingStrategy, useSortable,
  sortableKeyboardCoordinates,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const TABS = [
  { id: "all", label: "All" },
  { id: "published", label: "Published" },
  { id: "draft", label: "Drafts" },
];

function GalleryCard({ item, onTogglePublish, onToggleFeatured, onDelete, reorderMode, selectMode, selected, onToggleSelect }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: item.id,
    disabled: !reorderMode,
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  };

  const handleCardClick = (e) => {
    if (!selectMode) return;
    e.preventDefault();
    onToggleSelect(item.id);
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      data-testid={`gallery-row-${item.id}`}
      onClick={handleCardClick}
      className={`bg-[#0f0f0f] border flex flex-col group transition-colors ${
        selected ? "border-[#4A6D8C] ring-2 ring-[#4A6D8C]/40" : "border-[#1a1a1a]"
      } ${reorderMode ? "cursor-grab active:cursor-grabbing ring-1 ring-[#4A6D8C]/30" : selectMode ? "cursor-pointer" : ""}`}
    >
      <div className="relative aspect-[4/3] bg-[#0a0a0a] overflow-hidden">
        <img
          src={resolveMedia(item.thumbnail || item.image) || videoThumbUrl(item)}
          alt={item.alt_text || item.title}
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
          loading="lazy"
        />
        {reorderMode && (
          <span
            {...attributes}
            {...listeners}
            className="absolute top-2 left-2 bg-black/70 border border-white/15 p-1.5 text-white backdrop-blur-md"
            data-testid={`drag-handle-${item.id}`}
            aria-label="Drag to reorder"
          >
            <GripVertical size={14} />
          </span>
        )}
        {selectMode && (
          <span
            className={`absolute top-2 left-2 p-1.5 backdrop-blur-md border ${
              selected ? "bg-[#4A6D8C] border-[#4A6D8C] text-white" : "bg-black/60 border-white/15 text-white"
            }`}
            data-testid={`select-${item.id}`}
          >
            {selected ? <CheckSquare size={14} /> : <Square size={14} />}
          </span>
        )}
        {item.type === "video" && (
          <span className="absolute top-2 left-1/2 -translate-x-1/2 text-[9px] tracking-[0.22em] uppercase bg-black/60 border border-white/20 px-2 py-1 flex items-center gap-1">
            <Play size={10} fill="currentColor" /> Video
          </span>
        )}
        {item.status === "draft" && (
          <span className="absolute top-2 right-2 text-[9px] tracking-[0.22em] uppercase bg-[#4A6D8C]/80 px-2 py-1">
            Draft
          </span>
        )}
        {item.featured && (
          <span className="absolute bottom-2 right-2 text-[#4A6D8C]">
            <Star size={14} fill="currentColor" />
          </span>
        )}
        {item.upscaled && (
          <span className="absolute bottom-2 left-2 text-[9px] tracking-[0.22em] uppercase bg-[#0f1f2e] border border-[#4A6D8C] text-[#9ec3e0] px-2 py-1">
            HiRes
          </span>
        )}
      </div>
      <div className="p-3 flex-1 flex flex-col">
        <div className="text-[10px] tracking-[0.22em] uppercase text-[#737373]">
          {item.category}
        </div>
        <div className="font-display text-sm tracking-tight mt-1 leading-tight line-clamp-2">
          {item.title}
        </div>
        <div className="text-[11px] text-[#A3A3A3] mt-1 truncate">
          {item.client || "-"}
        </div>
        {!reorderMode && !selectMode && (
          <div className="flex items-center justify-between mt-3 pt-3 border-t border-[#1a1a1a]">
            <button
              onClick={() => onTogglePublish(item)}
              data-testid={`toggle-publish-${item.id}`}
              className="text-[#A3A3A3] hover:text-white"
              title={item.status === "published" ? "Unpublish" : "Publish"}
            >
              {item.status === "published" ? <Eye size={14} /> : <EyeOff size={14} />}
            </button>
            <button
              onClick={() => onToggleFeatured(item)}
              data-testid={`toggle-featured-${item.id}`}
              className={item.featured ? "text-[#658CAE]" : "text-[#737373] hover:text-white"}
              title="Feature on grid"
            >
              <Star size={14} fill={item.featured ? "currentColor" : "none"} />
            </button>
            <Link
              to={`/admin/gallery/${item.id}`}
              data-testid={`edit-${item.id}`}
              className="text-[#A3A3A3] hover:text-white"
              title="Edit"
            >
              <Pencil size={14} />
            </Link>
            <button
              onClick={() => onDelete(item)}
              data-testid={`delete-${item.id}`}
              className="text-[#A3A3A3] hover:text-red-300"
              title="Delete"
            >
              <Trash2 size={14} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default function AdminGalleryList() {
  const [items, setItems] = useState([]);
  const [tab, setTab] = useState("all");
  const [loading, setLoading] = useState(true);
  const [reorderMode, setReorderMode] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [savingOrder, setSavingOrder] = useState(false);
  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [backfilling, setBackfilling] = useState(false);
  const [backfillProgress, setBackfillProgress] = useState(null); // { processed, total }

  const runBackfill = async (override = false) => {
    const msg = override
      ? `Re-detect kit + regenerate Behind-the-Lens on EVERY photo?\n\nThis OVERWRITES previous AI suggestions and may take several minutes.`
      : `Run AI on every photo missing kit / Behind-the-Lens / tech details?\n\nThis runs in the background and may take a few minutes.`;
    if (!window.confirm(msg)) return;
    setBackfilling(true);
    setBackfillProgress(null);
    try {
      const job = await adminBackfillTech({ override });
      if (!job?.job_id) {
        toast.error("Backfill failed to start.");
        setBackfilling(false);
        return;
      }
      toast.message(`${override ? "Re-detect" : "Backfill"} started · ${job.total} photos queued`, { id: "backfill" });
      const final = await adminPollBackfill(job.job_id, {
        onProgress: (st) => {
          setBackfillProgress({
            processed: st.processed || 0,
            skipped: st.skipped || 0,
            total: st.total || job.total,
          });
          toast.message(
            `Backfill · ${(st.processed || 0) + (st.skipped || 0)}/${st.total || job.total}`,
            { id: "backfill", description: `processed ${st.processed || 0} · skipped ${st.skipped || 0}${(st.errors || []).length ? ` · ${st.errors.length} errors` : ""}` }
          );
        },
      });
      if (final.status === "completed") {
        toast.success(
          `Backfill complete · processed ${final.processed} · skipped ${final.skipped}${(final.errors || []).length ? ` · ${final.errors.length} errors` : ""}`,
          { id: "backfill" }
        );
        load(tab);
      } else if (final.status === "failed") {
        toast.error(final.error || "Backfill failed.", { id: "backfill" });
      } else if (final.status === "disconnected") {
        toast.error("Lost connection to backfill job, refresh later.", { id: "backfill" });
      } else if (final.status === "timeout") {
        toast.warning("Backfill still running, refresh later.", { id: "backfill" });
      }
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message;
      toast.error(typeof detail === "string" ? detail : "Backfill failed.");
    } finally {
      setBackfilling(false);
      setBackfillProgress(null);
    }
  };

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const load = (t) => {
    setLoading(true);
    const status = t === "all" ? undefined : t;
    adminListGallery(status)
      .then(setItems)
      .catch(() => toast.error("Couldn't load gallery."))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load(tab);
  }, [tab]);

  const switchTab = (t) => {
    setTab(t);
    setReorderMode(false);
    setDirty(false);
    setSelectMode(false);
    setSelected(new Set());
  };

  const togglePublish = async (item) => {
    const next = item.status === "published" ? "draft" : "published";
    try {
      const updated = await adminUpdateGallery(item.id, { status: next });
      setItems((prev) => prev.map((i) => (i.id === item.id ? updated : i)));
      toast.success(next === "published" ? "Published." : "Moved to drafts.");
    } catch {
      toast.error("Update failed.");
    }
  };

  const toggleFeatured = async (item) => {
    try {
      const updated = await adminUpdateGallery(item.id, { featured: !item.featured });
      setItems((prev) => prev.map((i) => (i.id === item.id ? updated : i)));
    } catch {
      toast.error("Update failed.");
    }
  };

  const removeItem = async (item) => {
    if (!window.confirm(`Delete "${item.title}"? This cannot be undone.`)) return;
    try {
      await adminDeleteGallery(item.id);
      setItems((prev) => prev.filter((i) => i.id !== item.id));
      toast.success("Deleted.");
    } catch {
      toast.error("Delete failed.");
    }
  };

  const onDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = items.findIndex((i) => i.id === active.id);
    const newIndex = items.findIndex((i) => i.id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    setItems((prev) => arrayMove(prev, oldIndex, newIndex));
    setDirty(true);
  };

  const saveOrder = async () => {
    setSavingOrder(true);
    try {
      const payload = items.map((it, idx) => ({ id: it.id, order: idx + 1 }));
      await adminReorderGallery(payload);
      setDirty(false);
      toast.success("Order saved.");
    } catch {
      toast.error("Couldn't save order.");
    } finally {
      setSavingOrder(false);
    }
  };

  const cancelReorder = () => {
    setReorderMode(false);
    if (dirty) load(tab);
    setDirty(false);
  };

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAll = () => {
    if (selected.size === items.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(items.map((i) => i.id)));
    }
  };

  const runBulk = async (action) => {
    const ids = Array.from(selected);
    if (ids.length === 0) {
      toast.error("Select at least one item.");
      return;
    }
    if (action === "delete" && !window.confirm(`Delete ${ids.length} item${ids.length !== 1 ? "s" : ""}? This cannot be undone.`)) {
      return;
    }
    setBulkBusy(true);
    try {
      await adminBulkGallery(ids, action);
      toast.success(`${action} · ${ids.length} item${ids.length !== 1 ? "s" : ""}.`);
      setSelected(new Set());
      load(tab);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Bulk action failed.");
    } finally {
      setBulkBusy(false);
    }
  };

  const exitSelect = () => {
    setSelectMode(false);
    setSelected(new Set());
  };

  return (
    <div className="px-6 lg:px-10 py-10">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <div className="text-[10px] tracking-[0.32em] uppercase text-[#4A6D8C] mb-2">
            Gallery
          </div>
          <h1 className="font-display text-4xl tracking-tight">Manage gallery</h1>
        </div>
        <div className="flex gap-3 flex-wrap">
          {!reorderMode && !selectMode && (
            <>
              <button
                onClick={() => setSelectMode(true)}
                data-testid="select-toggle"
                className="hl-btn-ghost inline-flex items-center gap-2"
              >
                <CheckSquare size={14} /> Select
              </button>
              <button
                onClick={() => setReorderMode(true)}
                data-testid="reorder-toggle"
                className="hl-btn-ghost inline-flex items-center gap-2"
              >
                <GripVertical size={14} /> Reorder
              </button>
              <Link
                to="/admin/gallery/bulk"
                data-testid="bulk-upload-link"
                className="hl-btn-ghost inline-flex items-center gap-2"
              >
                <UploadCloud size={14} /> Bulk upload
              </Link>
              <Link
                to="/admin/gallery/import-videos"
                data-testid="video-import-link"
                className="hl-btn-ghost inline-flex items-center gap-2"
              >
                <Clapperboard size={14} /> Import videos
              </Link>
              <button
                type="button"
                onClick={() => runBackfill(false)}
                disabled={backfilling}
                data-testid="backfill-tech-button"
                className="hl-btn-ghost inline-flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
                title="Run AI to fill in kit + Behind-the-Lens + tech suggestions on any photos missing them"
              >
                {backfilling ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
                {backfilling
                  ? backfillProgress
                    ? `Backfilling · ${(backfillProgress.processed || 0) + (backfillProgress.skipped || 0)}/${backfillProgress.total || "?"}`
                    : "Backfilling…"
                  : "AI fill missing"}
              </button>
              <button
                type="button"
                onClick={() => runBackfill(true)}
                disabled={backfilling}
                data-testid="backfill-override-button"
                className="hl-btn-ghost inline-flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
                title="Re-run AI on EVERY photo, re-detects kit and regenerates Behind the Lens, overwriting previous AI suggestions"
              >
                {backfilling ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
                AI re-detect all
              </button>
              <Link
                to="/admin/gallery/new"
                data-testid="gallery-new-button"
                className="hl-btn-primary inline-flex items-center gap-2"
              >
                <Plus size={14} /> Upload image
              </Link>
            </>
          )}
          {reorderMode && (
            <>
              <button onClick={cancelReorder} data-testid="reorder-cancel" className="hl-btn-ghost">
                Cancel
              </button>
              <button
                onClick={saveOrder}
                disabled={!dirty || savingOrder}
                data-testid="reorder-save"
                className="hl-btn-primary inline-flex items-center gap-2 disabled:opacity-60"
              >
                <Save size={14} /> {savingOrder ? "Saving…" : "Save order"}
              </button>
            </>
          )}
          {selectMode && (
            <button onClick={exitSelect} data-testid="select-cancel" className="hl-btn-ghost inline-flex items-center gap-2">
              <XCircle size={14} /> Exit selection
            </button>
          )}
        </div>
      </div>

      {!reorderMode && !selectMode && (
        <div className="mt-8 flex gap-2" data-testid="gallery-tabs">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => switchTab(t.id)}
              data-testid={`gallery-tab-${t.id}`}
              className={`hl-chip ${tab === t.id ? "active" : ""}`}
            >
              {t.label}
            </button>
          ))}
        </div>
      )}

      {reorderMode && (
        <div className="mt-6 text-[12px] text-[#A3A3A3] bg-[#0f0f0f] border border-[#1a1a1a] p-4">
          Drag any tile by the handle to change its position. Featured items still
          appear first on the public grid; everything else follows this order.
          {dirty && <span className="text-[#658CAE] ml-2">· unsaved changes</span>}
        </div>
      )}

      {/* Bulk action bar */}
      {selectMode && (
        <div
          className="mt-6 sticky top-2 z-20 bg-[#0f0f0f]/95 backdrop-blur-md border border-[#1a1a1a] p-4 flex flex-wrap items-center gap-3"
          data-testid="bulk-action-bar"
        >
          <button
            onClick={selectAll}
            data-testid="bulk-select-all"
            className="text-[11px] tracking-[0.22em] uppercase text-[#A3A3A3] hover:text-white inline-flex items-center gap-2"
          >
            {selected.size === items.length && items.length > 0 ? <CheckSquare size={14} /> : <Square size={14} />}
            {selected.size} / {items.length} selected
          </button>
          <span className="text-[#525252] mx-1">·</span>
          <button onClick={() => runBulk("publish")} disabled={bulkBusy || selected.size === 0} data-testid="bulk-publish" className="hl-btn-ghost text-[11px] inline-flex items-center gap-2 disabled:opacity-40">
            <Eye size={12} /> Publish
          </button>
          <button onClick={() => runBulk("unpublish")} disabled={bulkBusy || selected.size === 0} data-testid="bulk-unpublish" className="hl-btn-ghost text-[11px] inline-flex items-center gap-2 disabled:opacity-40">
            <EyeOff size={12} /> Unpublish
          </button>
          <button onClick={() => runBulk("feature")} disabled={bulkBusy || selected.size === 0} data-testid="bulk-feature" className="hl-btn-ghost text-[11px] inline-flex items-center gap-2 disabled:opacity-40">
            <Star size={12} /> Feature
          </button>
          <button onClick={() => runBulk("unfeature")} disabled={bulkBusy || selected.size === 0} data-testid="bulk-unfeature" className="hl-btn-ghost text-[11px] inline-flex items-center gap-2 disabled:opacity-40">
            <Star size={12} /> Un-feature
          </button>
          <button onClick={() => runBulk("delete")} disabled={bulkBusy || selected.size === 0} data-testid="bulk-delete" className="hl-btn-ghost text-[11px] inline-flex items-center gap-2 disabled:opacity-40 hover:!border-red-400 hover:!text-red-200">
            <Trash2 size={12} /> Delete
          </button>
        </div>
      )}

      <div className="mt-8">
        {loading ? (
          <div className="text-[#737373] text-xs tracking-[0.24em] uppercase py-20">
            Loading…
          </div>
        ) : items.length === 0 ? (
          <div className="text-[#737373] text-xs tracking-[0.24em] uppercase py-20">
            Nothing here yet.
          </div>
        ) : (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
            <SortableContext items={items.map((i) => i.id)} strategy={rectSortingStrategy}>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
                {items.map((item) => (
                  <GalleryCard
                    key={item.id}
                    item={item}
                    reorderMode={reorderMode}
                    selectMode={selectMode}
                    selected={selected.has(item.id)}
                    onToggleSelect={toggleSelect}
                    onTogglePublish={togglePublish}
                    onToggleFeatured={toggleFeatured}
                    onDelete={removeItem}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        )}
      </div>
    </div>
  );
}
